<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="general-details" data-cy="general-details-page">
    <cd-modal
      class="modal"
      :can-cancel="false"
      :large="true"
      title="Don't have a Tax ID?"
      :visible="isModalVisible"
      @close="isModalVisible = false"
      @confirm="isModalVisible = false"
    >
      <p
        class="modal-text"
        v-html="$t('smsRegistrations.generalDetailsWarningModalText')"
      ></p>
    </cd-modal>
    <div class="general-details-header">
      <h4>{{ $t("smsRegistrations.generalDetailsHeader") }}</h4>
    </div>
    <div class="general-details-form">
      <VSelect
          v-if="this.soleProprietorEnabled"
          data-cy="sms-registration-has-tax-id-select"
          class="full-width-field"
          @input="hasTaxIdChanged"
          :value="hasTaxId
              ? 'true'
              : hasTaxId === false
                  ? 'false'
                  : ''"
          :label="$t('smsRegistrations.taxIdQuestion')"
          placeholder="Select an answer"
          :disabled="isEditView || readOnly"
      >
        <VOption
            v-for="option in taxIdOptions"
            :data-cy="`tax-id-`+ option.value"
            :key="`tax-id-${option.value}`"
            :value="option.value.toString()"
            :text="option.label"
        />
      </VSelect>
      <v-divider v-if="hasTaxId !== undefined" orientation="horizontal" class="divider"/>
      <template v-if="hasTaxId !== undefined">
        <div :class="hasTaxId ? 'warning-message-wrapper' : 'info-message-wrapper'">
          <vwc-icon
              :class="hasTaxId ? 'warning-icon' : 'info-icon'"
              :type="hasTaxId ? 'warning-line' : 'info-line'"
          />
          <span v-html="hasTaxId ? $t('smsRegistrations.generalDetailsWarning') : $t('smsRegistrations.generalDetailsWarningSoleProprietor')"></span>
        </div>
        <text-field
            :disabled="isEditView || readOnly"
            data-cy="sms-registration-company-name-input"
            class="tex-field"
            @textChanged="registrationDetails.companyName = $event"
            :value="registrationDetails.companyName"
            dense
            :label="hasTaxId ? $t('smsRegistrations.companyName') : $t('smsRegistrations.brandName')"
            :placeholder="hasTaxId ? $t('smsRegistrations.companyNamePlaceHolder') : $t('smsRegistrations.brandName')"
        />
        <div class="tooltip-wrapper-div">
          <text-field
              :disabled="readOnly"
              data-cy="sms-registration-dba-input"
              class="tex-field"
              @textChanged="registrationDetails.displayName= $event"
              :value="registrationDetails.displayName"
              dense
              :label="$t('smsRegistrations.displayName')"
              :placeholder="$t('smsRegistrations.displayName')"
          />
          <VButton id="dba-wrapper-tooltip-anchor" size="condensed" appearance="ghost-light" icon="help-line" connotation="accent" shape="rounded" />
          <VTooltip style="--tooltip-inline-size: 300px;" anchor="dba-wrapper-tooltip-anchor" :text="$t('smsRegistrations.displayNameTooltip')" placement="left" />
        </div>
        <div v-if="hasUsTaxId && hasTaxId !== false" class="tax-id-wrapper">
          <text-field
              :disabled="isEditView || readOnly"
              data-cy="sms-registration-ein-tax-id-input"
              class="tex-field"
              @textChanged="registrationDetails.ein = $event"
              :value="registrationDetails.ein"
              :pattern="regexPatterns.einPattern"
              :validation-message="einErrorMessage"
              dense
              :label="$t('smsRegistrations.ein')"
              :placeholder="$t('smsRegistrations.ein')"
          />
        </div>
        <div id="canada-tax-id-wrapper" v-else-if="isCanada">
          <text-field
              id="canada-tax-id"
              :disabled="isEditView || readOnly"
              @textChanged="registrationDetails.ein = $event"
              :pattern="regexPatterns.canadaTaxIdPattern"
              :validation-message="canadaTaxIdErrorMessage"
              :value="registrationDetails.ein"
              dense
              :label="$t('smsRegistrations.canadaTaxId')"
          />
        </div>
        <vwc-select
            :disabled="readOnly"
            data-cy="sms-registration-vertical-type-select"
            dense
            required
            class="text-field"
            @selected="verticalChanged"
            :label="$t('smsRegistrations.vertical')"
        >
          <vwc-list-item
              v-for="vertical in verticalOptions"
              :selected="vertical.value === registrationDetails.vertical"
              :key="vertical.value"
              :value="vertical.label"
              tabindex="-1"
              aria-disabled="false"
              role="option"
          >{{ vertical.label }}</vwc-list-item
          >
        </vwc-select>
        <text-field
            v-if="hasTaxId"
            :disabled="isEditView || readOnly"
            data-cy="sms-registration-contact-name-input"
            @textChanged="registrationDetails.contactName = $event"
            :value="registrationDetails.contactName"
            dense
            :label="$t('smsRegistrations.contactName')"
            :placeholder="$t('smsRegistrations.contactName')"
        />
        <text-field
            v-if="hasTaxId === false"
            :disabled="readOnly"
            key="ownerFirstName"
            data-cy="sms-registration-owner-first-name-input"
            class="text-field"
            @textChanged="registrationDetails.firstName = $event"
            :value="registrationDetails.firstName"
            dense
            :label="$t('smsRegistrations.ownerFirstName')"
            :placeholder="$t('smsRegistrations.ownerFirstName')"
        />
        <text-field
            v-if="hasTaxId === false"
            :disabled="readOnly"
            key="ownerLastName"
            data-cy="sms-registration-owner-last-name-input"
            class="text-field"
            @textChanged="registrationDetails.lastName = $event"
            :value="registrationDetails.lastName"
            dense
            :label="$t('smsRegistrations.ownerLastName')"
            :placeholder="$t('smsRegistrations.ownerLastName')"
        />
        <vwc-select
            data-cy="sms-registration-country-input"
            required
            class="text-field"
            @selected="countryChanged"
            dense=""
            :disabled="isEditView || readOnly"
            :label="$t('smsRegistrations.country')"
        >
          <vwc-list-item
              v-for="country in countryOptions"
              :selected="country.value === registrationDetails.country"
              :key="country.label"
              :value="country.value"
              tabindex="-1"
              aria-disabled="false"
              role="option"
          >{{ country.label }}</vwc-list-item
          >
        </vwc-select>
        <div class="tooltip-wrapper-div">
          <vlt-field
              :label="$t('smsRegistrations.phone')"
              class="phone-field"
              :error="phoneErrorMessage"
              data-cy="phone-number-field"
          >
            <nx-phone-input
                v-model="registrationDetails.e164"
                :big="false"
                :countries="countryOptions"
                :default-country-code="registrationDetails.country"
                :disabled="readOnly"
                :country-select-disabled="true"
                @validate="onPhoneValidation"
            />
          </vlt-field>
          <VButton id="phone-tooltip-anchor" size="condensed" appearance="ghost-light" icon="help-line" connotation="accent" shape="rounded" />
          <VTooltip style="--tooltip-inline-size: 300px" anchor="phone-tooltip-anchor" :text="$t('smsRegistrations.phoneTooltip')" placement="left"/>
        </div>
        <div v-if="hasTaxId === false" class="tooltip-wrapper-div">
          <text-field
              data-cy="sms-registration-mobile-phone-input"
              :validation-message="mobilePhoneErrorMessage"
              :pattern="regexPatterns.mobilePhoneRegex"
              :value="registrationDetails.mobilePhone"
              @textChanged="registrationDetails.mobilePhone = $event"
              dense
              :label="$t('smsRegistrations.mobilePhone')"
              :placeholder="$t('smsRegistrations.mobilePhonePlaceholder')"
              :disabled="readOnly"
          />
          <VButton id="mobile-phone-tooltip-anchor" size="condensed" appearance="ghost-light" icon="help-line" connotation="accent" shape="rounded" />
          <VTooltip style="--tooltip-inline-size: 300px" anchor="mobile-phone-tooltip-anchor" :text="$t('smsRegistrations.mobilePhoneTooltip')" placement="left"/>
        </div>
        <div class="tooltip-wrapper-div">
          <text-field
              :disabled="readOnly"
              data-cy="sms-registration-email-input"
              :validation-message="emailErrorMessage"
              :pattern="regexPatterns.emailRegex"
              :value="registrationDetails.email"
              @textChanged="registrationDetails.email = $event"
              dense
              :label="$t('smsRegistrations.email')"
              :placeholder="$t('smsRegistrations.email')"
          />
          <VButton id="registration-email-tooltip-anchor" size="condensed" appearance="ghost-light" icon="help-line" connotation="accent" shape="rounded" />
          <VTooltip style="--tooltip-inline-size: 300px" anchor="registration-email-tooltip-anchor" :text="$t('smsRegistrations.emailTooltip')" placement="left"/>
        </div>
        <text-field
            :disabled="readOnly"
            data-cy="sms-registration-street-input"
            @textChanged="registrationDetails.street = $event"
            :value="registrationDetails.street"
            dense
            :label="$t('smsRegistrations.street')"
            :placeholder="$t('smsRegistrations.street')"
        />
        <text-field
            :disabled="readOnly"
            data-cy="sms-registration-city-input"
            @textChanged="registrationDetails.city = $event"
            :value="registrationDetails.city"
            dense
            :label="$t('smsRegistrations.city')"
            :placeholder="$t('smsRegistrations.city')"
        />
        <text-field
            :disabled="readOnly"
            data-cy="sms-registration-state-input"
            :validation-message="stateErrorMessage"
            :pattern="statePattern"
            @textChanged="registrationDetails.state = $event"
            :value="registrationDetails.state"
            dense
            :label="$t('smsRegistrations.state')"
            :placeholder="$t('smsRegistrations.state')"
        />
        <text-field
            :disabled="readOnly"
            data-cy="sms-registration-postal-code-input"
            :validation-message="zipErrorMessage"
            :pattern="
          isCanada ? regexPatterns.canadaZipPattern : regexPatterns.zipPattern
        "
            @textChanged="registrationDetails.postalCode = $event"
            :value="registrationDetails.postalCode"
            dense
            :label="$t('smsRegistrations.postalCode')"
            :placeholder="$t('smsRegistrations.postalCode')"
        />
        <text-field
            :disabled="readOnly"
            data-cy="sms-registration-website-input"
            :validation-message="webSiteValidation"
            :pattern="regexPatterns.webSiteRegex"
            @textChanged="onWebsiteTextChanged"
            :value="registrationDetails.website"
            dense
            :label="$t('smsRegistrations.website')"
            :placeholder="$t('smsRegistrations.website')"
        />

        <template v-if="hasTaxId === true">
          <div class="entity-type-radio-group">
            <span>Entity type</span>
            <div class="radio-options-container">
              <div
                  v-for="entityType in entityTypeOptions"
                  :key="entityType.value"
                  class="radio-option"
              >
                <vwc-radio
                    :id="'entity-type-' + entityType.value"
                    :value="entityType.value"
                    :checked="entityType.value === registrationDetails.entityType"
                    :disabled="isEditView || readOnly"
                    @change="entityTypeChanged(entityType.value)"
                    :data-cy="'sms-registration-entity-type-' + entityType.value.toLowerCase()"
                ></vwc-radio>
                <label
                    :for="'entity-type-' + entityType.value"
                    @click="!isEditView && entityTypeChanged(entityType.value)"
                >{{ entityType.label }}</label>
              </div>
            </div>
          </div>
        </template>

        <template v-if="shouldShowStockFields">
          <vwc-select
              :disabled="readOnly"
              dense
              required
              id="stock-exchange-select"
              class="text-field"
              :label="$t('smsRegistrations.stockExchange')"
              @selected="stockExchangeChanged"
              data-cy="sms-registration-stock-exchange-select"
          >
            <vwc-list-item
                v-for="stockExchange in stockExchangeForCountry"
                :key="stockExchange.id"
                :value="stockExchange.id"
                :selected="stockExchange.id === registrationDetails.stockExchange"
                tabindex="-1"
                aria-disabled="false"
                role="option"
            >{{ stockExchange.description }} ({{ stockExchange.id }})</vwc-list-item
            >
          </vwc-select>
          <text-field
              :disabled="readOnly"
              data-cy="sms-registration-stock-symbol"
              dense
              required
              @textChanged="registrationDetails.stockSymbol = $event"
              :value="registrationDetails.stockSymbol"
              :label="$t('smsRegistrations.stockSymbol')"
              :placeholder="$t('smsRegistrations.stockSymbol')"
          />
          <div class="tooltip-wrapper-div">
            <text-field
                :disabled="readOnly"
                data-cy="sms-registration-business-contact-email"
                :validation-message="verificationEmailValidation"
                :pattern="regexPatterns.emailRegex"
                :value="registrationDetails.verificationEmail"
                @textChanged="registrationDetails.verificationEmail = $event"
                dense
                :label="$t('smsRegistrations.publicProfitEmailLabel')"
                :placeholder="$t('smsRegistrations.publicProfitEmailLabel')"
                :customValidator="validateEmailWithDomain"
                ref="verificationEmail"
            />
            <VButton id="business-contact-email-tooltip-anchor" size="condensed" appearance="ghost-light" icon="help-line" connotation="accent" shape="rounded" />
            <VTooltip style="--tooltip-inline-size: 300px" anchor="business-contact-email-tooltip-anchor" :text="$t('smsRegistrations.publicProfitEmailTooltip')" placement="left"/>
          </div>
        </template>
        <vwc-select
            v-if="isAllowToChangeVolume"
            :disabled="readOnly"
            data-cy="sms-registration-volume-type"
            required
            class="text-field"
            @selected="volumeTypeChanged"
            dense
            label="Volume Type"
        >
          <vwc-list-item
              v-for="volumeType in volumeTypeOptions"
              :selected="volumeType.value === registrationDetails.volumeType"
              :key="volumeType.value"
              :value="volumeType.value"
              tabindex="-1"
              aria-disabled="false"
              role="option"
          >{{ volumeType.label }}</vwc-list-item
          >
        </vwc-select>
      </template>
    </div>
  </div>
</template>

<!-- eslint-disable vue/no-mutating-props -->
<script>
import TextField from "../VwcTextField";
import NxPhoneInput from "../NxPhoneInput";
import { VltField } from "@vonagevolta/vue";
import '@vonage/vwc-radio';
import {countriesMapForSmsRegistration} from "@/helpers/countriesMap";
import { NON_QUICKFLOW_VERTICALS, VERTICALS } from "@/helpers/brands/constants";
import clickOutside from "@/directives/clickOutside";
import CdModal from "../atomicUIComponents/modal/CdModal";
import {
  CANADA_TAX_ID_REGEX,
  CANADA_ZIP_REGEX,
  EIN_REGEX,
  EMAIL_REGEX,
  E164_PHONE_REGEX,
  VALID_VERIFICATION_EMAIL_REGEX,
  STATE_REGEX,
  WEBSITE_REGEX,
  ZIP_REGEX,
} from "@/helpers/constants";
import index, {mapActions, mapGetters, mapState} from "vuex";
import get from "lodash.get";
import {allowedHighVolumeCreation} from "@/helpers/functions";
import {VButton, VDivider, VOption, VSelect, VTooltip} from "@vonage/vivid-vue";

const taxIdOptions = [
  {
    value: "true",
    label: "Yes",
  },
  {
    value: "false",
    label: "No",
  },
];
const countryOptions = Object.values(countriesMapForSmsRegistration).map((country) => ({
  label: country.name,
  value: country.code,
}));
const entityTypeOptions = [
  {
    value: "PRIVATE_PROFIT",
    label: "Private profit",
  },
  {
    value: "NON_PROFIT",
    label: "Non profit",
  },
  {
    value: "GOVERNMENT",
    label: "Govermental",
  },
  {
    value: "PUBLIC_PROFIT",
    label: "Public profit",
  },
];

const volumeTypeOptions = [
  {
    value: process.env.VUE_APP_CAMPAIGN_HIGH_USE_CASE,
    label: "High Volume",
  },
  {
    value: process.env.VUE_APP_CAMPAIGN_LOW_USE_CASE,
    label: "Low Volume",
  },
];
const UsCountryCodes = ["AS", "GU", "MP", "US", "UM", "VI", "PR"];

export default {
  name: "GeneralDetails",
  components: {
    VDivider,
    TextField,
    NxPhoneInput,
    VltField,
    CdModal,
    VButton,
    VTooltip,
    VOption,
    VSelect
  },
  props: {
    registrationDetails: {
      type: Object,
      default: () => ({}),
    },
    isEditView: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      taxIdOptions,
      countryOptions,
      entityTypeOptions,
      volumeTypeOptions,
      hasTaxId: undefined,
      isModalVisible: false,
      regexPatterns: {
        stateRegex: STATE_REGEX,
        webSiteRegex: WEBSITE_REGEX,
        emailRegex: EMAIL_REGEX,
        mobilePhoneRegex: E164_PHONE_REGEX,
        supportEmailRegex: VALID_VERIFICATION_EMAIL_REGEX,
        einPattern: EIN_REGEX,
        zipPattern: ZIP_REGEX,
        canadaTaxIdPattern: CANADA_TAX_ID_REGEX,
        canadaZipPattern: CANADA_ZIP_REGEX,
      },
      phoneValidation: false,
      phoneErrorMessage: "",
    };
  },
  directives: {
    "click-outside": clickOutside,
  },
  created() {
    if (this.registrationDetails.hasTaxId !== undefined) {
      this.hasTaxId = this.registrationDetails.hasTaxId;
    }
  },
  async beforeMount() {
    try {
      await this.checkSpFlagForUser()
          .then(() => {
            if (!this.soleProprietorEnabled) {
              this.hasTaxIdChanged({ target: { currentValue: "true" }});
            }
          })
          .catch(error => {
            console.error('[GeneralDetails] Error checking SP flag:', error);
            this.hasTaxIdChanged({ target: { currentValue: "true" }});
          });
    } catch (error) {
      console.error('[GeneralDetails] beforeMount error:', error);
      this.hasTaxIdChanged({ target: { currentValue: "true" }});
    }
  },
  methods: {
    ...mapActions(["sendAmplitudeEvent"]),
    ...mapActions("smsRegistrations", ["checkSpFlagForUser"]),
    hasTaxIdChanged(event) {
      this.hasTaxId = event.target.currentValue === "true";
      this.$set(this.registrationDetails, 'hasTaxId', this.hasTaxId);

      if (!this.registrationDetails.entityType || this.hasTaxId === false) {
        // Only set entityType if it's not already set or if we're changing to no tax ID
        this.$set(this.registrationDetails, 'entityType', this.hasTaxId === false ? 'SOLE_PROPRIETOR' : '');
      }
    },
    countryChanged(value) {
      if (value.detail && value.detail.index !== -1) {
        this.registrationDetails.country =
          countryOptions[value.detail.index].value;
      }
    },
    verticalChanged(value) {
      if (value.detail.index !== -1) {
        this.registrationDetails.vertical =
          this.verticalOptions[value.detail.index].value;
      }
    },
    onPhoneValidation(valid, _country, value) {
      this.phoneValidation = valid;
      this.phoneErrorMessage = this.getPhoneErrorMessage(valid, value);
    },
    getPhoneErrorMessage(valid, value) {
      if (!value) {
        return this.$t("smsRegistrations.requiredField");
      }
      if (!valid) {
        return this.$t("smsRegistrations.invalidPhone");
      }
      return "";
    },
    entityTypeChanged(value) {
      this.$set(this.registrationDetails, 'entityType', value);

      this.$nextTick(() => {
        this.$forceUpdate();
        if (this.$refs.verificationEmail) {
          this.$refs.verificationEmail.$forceUpdate();
        }
        if (value === 'PUBLIC_PROFIT') {
          this.scrollToNewFields();
        }
      });

      this.$emit('entity-type-changed', value);
    },
    volumeTypeChanged(value) {
      if (value?.detail?.index > -1) {
        this.registrationDetails.volumeType =
            volumeTypeOptions[value.detail.index].value;
      }
    },
    stockExchangeChanged(value) {
      if (value?.detail?.index > -1) {
        this.registrationDetails.stockExchange =
          this.stockExchangeForCountry[value.detail.index].value;
      }
    },
    onWebsiteTextChanged($event) {
      this.registrationDetails.website = $event
      this.$nextTick(() => {
        if (this.$refs.verificationEmail) {
          this.$refs.verificationEmail.$forceUpdate();
        }
      });
    },
    validateEmailWithDomain(email) {
      const website = this.registrationDetails.website;

      if (!email) {
        return this.$t('smsRegistrations.requiredField');
      }

      if (!website) {
        return this.$t('smsRegistrations.websiteRequired');
      }

      const emailRegex = new RegExp(this.regexPatterns.emailRegex);
      if (!emailRegex.test(email)) {
        return this.$t('smsRegistrations.invalidEmail');
      }

      const emailSupportRegex = new RegExp(this.regexPatterns.supportEmailRegex);
      if (!emailSupportRegex.test(email)) {
        return this.$t('smsRegistrations.invalidSupportEmail');
      }

      if (!this.isVerificationEmailValid(email, website)) {
        return this.$t('smsRegistrations.emailDoesNotMatchWebsite');
      }

      return true;
    },
    isVerificationEmailValid(email, website) {
      if (!email || !website) {
        return false;
      }

      const emailRegex = new RegExp(this.regexPatterns.emailRegex);
      const emailSupportRegex = new RegExp(this.regexPatterns.supportEmailRegex);

      if (!emailRegex.test(email) || !emailSupportRegex.test(email)) {
        return false;
      }

      const emailDomain = email.split('@')[1];
      const websiteDomain = website.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];

      return emailDomain === websiteDomain;
    },
    scrollToNewFields() {
      // Wait for the DOM to update
      this.$nextTick(() => {
        // Find the first new field (e.g., stock exchange field)
        const newField = this.$el.querySelector('#stock-exchange-select');
        if (newField) {
          newField.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  },

  computed: {
    index() {
      return index
    },
    ...mapGetters("enums", ["stockExchangeMap"]),
    ...mapState(['credentials']),
    ...mapState("smsRegistrations", ["soleProprietorEnabled"]),
    selectedTaxIdIndex() {
      if (this.registrationDetails.hasTaxId === undefined) return 0;
      return this.taxIdOptions.findIndex(option => option.value === this.registrationDetails.hasTaxId) + 1;
    },
    verticalOptions() {
      return [
        ...Object.values(VERTICALS)
          .filter(
            (vertical) => !NON_QUICKFLOW_VERTICALS.includes(vertical.value)
          )
          .map((option) => ({ ...option, label: this.$t(option.label) })),
      ];
    },
    mobilePhoneErrorMessage() {
      return this.registrationDetails.mobilePhone
        ? this.$t("smsRegistrations.invalidMobilePhone")
        : this.$t("smsRegistrations.requiredField");
    },
    emailErrorMessage() {
      return this.registrationDetails.email
        ? this.$t("smsRegistrations.invalidEmail")
        : this.$t("smsRegistrations.requiredField");
    },
    verificationEmailValidation() {
      return this.registrationDetails.verificationEmail
        ? this.$t("smsRegistrations.invalidEmail")
        : this.$t("smsRegistrations.requiredField");
    },
    stateErrorMessage() {
      if (this.isUS && this.registrationDetails.state) {
        return this.$t("smsRegistrations.unknownUsState");
      }
      return this.$t("smsRegistrations.requiredField");
    },
    einErrorMessage() {
      return this.registrationDetails.ein
        ? this.$t("smsRegistrations.invalidEin")
        : this.$t("smsRegistrations.requiredField");
    },
    zipErrorMessage() {
      return this.registrationDetails.postalCode
        ? this.isCanada
          ? this.$t("smsRegistrations.invalidZipCanada")
          : this.$t("smsRegistrations.invalidZip")
        : this.$t("smsRegistrations.requiredField");
    },
    statePattern() {
      if (this.registrationDetails.country === "US") {
        return this.regexPatterns.stateRegex;
      }
      return "";
    },
    webSiteValidation() {
      if (this.registrationDetails.website) {
        if (this.registrationDetails.website.length > 100) {
          return this.$t("smsRegistrations.websiteTooLong");
        }
        if (
          this.registrationDetails.website.startsWith("http://") ||
          this.registrationDetails.website.startsWith("https://")
        ) {
          return this.$t("smsRegistrations.invalidWebsite");
        }

        return this.$t("smsRegistrations.webSiteHttpLead");
      }
      return this.$t("smsRegistrations.requiredField");
    },
    generalDetailsValid() {
      let propsToValidate = Object.assign({}, this.registrationDetails);
      const isPublicProfit = this.registrationDetails.entityType === 'PUBLIC_PROFIT';

      delete propsToValidate.hasTaxId;
      if (!isPublicProfit) {
        delete propsToValidate.stockExchange;
        delete propsToValidate.stockSymbol;
        delete propsToValidate.verificationEmail;
      }

      if (this.hasTaxId === false) {
        delete propsToValidate.ein;
        delete propsToValidate.contactName;
      } else if (this.hasTaxId === true) {
        delete propsToValidate.firstName;
        delete propsToValidate.lastName;
        delete propsToValidate.mobilePhone;
      }

      const hasTaxIdSelected = this.hasTaxId !== undefined;

      const hasAllRequiredDetails = Object.values(propsToValidate).every(
          (detail) => detail?.trim().length > 0
      );
      const stateValidationUS = new RegExp(this.regexPatterns.stateRegex).test(
        this.registrationDetails.state
      );
      const stateValidationNotUS = this.registrationDetails.state?.length > 0;
      const streetValidation = this.registrationDetails.street?.length > 0;
      const stateValidation = this.isUS
        ? stateValidationUS
        : stateValidationNotUS;
      const emailValidation = new RegExp(this.regexPatterns.emailRegex).test(
        this.registrationDetails.email
      );
      const websiteValidation =
        new RegExp(this.regexPatterns.webSiteRegex).test(
          this.registrationDetails.website
        ) && this.registrationDetails.website.length < 101;

      let einValidation = true;
      if (this.hasTaxId === true) {
        const einValidationUs = new RegExp(this.regexPatterns.einPattern).test(
            this.registrationDetails.ein
        );
        const einValidationCa = new RegExp(
            this.regexPatterns.canadaTaxIdPattern
        ).test(this.registrationDetails.ein);
        einValidation = this.hasUsTaxId ? einValidationUs : einValidationCa;
      }

      const zipValidationUs = new RegExp(this.regexPatterns.zipPattern).test(
        this.registrationDetails.postalCode
      );
      const zipValidationCa = new RegExp(
        this.regexPatterns.canadaZipPattern
      ).test(this.registrationDetails.postalCode);
      const zipValidation = this.isCanada ? zipValidationCa : zipValidationUs;

      let mobilePhoneValidation = true;
      if (this.hasTaxId === false) {
        mobilePhoneValidation = new RegExp(this.regexPatterns.mobilePhoneRegex).test(
            this.registrationDetails.mobilePhone
        );
      }

      const verificationEmailValidation = !isPublicProfit ||
          this.isVerificationEmailValid(this.registrationDetails.verificationEmail, this.registrationDetails.website);

      return (
          hasAllRequiredDetails &&
          stateValidation &&
          emailValidation &&
          websiteValidation &&
          this.phoneValidation &&
          einValidation &&
          zipValidation &&
          streetValidation &&
          verificationEmailValidation &&
          hasTaxIdSelected &&
          mobilePhoneValidation
      );
    },
    isUS() {
      return this.registrationDetails.country === "US";
    },
    isAllowToChangeVolume() {
      return allowedHighVolumeCreation().includes(get(this.credentials, 'userId'));
    },
    hasUsTaxId() {
      return UsCountryCodes.includes(this.registrationDetails.country);
    },
    isCanada() {
      return this.registrationDetails.country === "CA";
    },
    canadaTaxIdErrorMessage() {
      return this.registrationDetails.ein
        ? this.$t("smsRegistrations.invalidCanadaTaxId")
        : this.$t("smsRegistrations.requiredField");
    },
    stockExchangeForCountry() {
      return Object.values(this.stockExchangeMap).filter(
        (exchange) => exchange.country === this.registrationDetails.country
      );
    },
    shouldShowStockFields() {
      return this.registrationDetails.entityType === "PUBLIC_PROFIT";
    },
  },
  watch: {
    'registrationDetails.hasTaxId': {
      immediate: true,
      handler(newValue) {
        if (newValue !== undefined && this.hasTaxId !== newValue) {
          this.hasTaxId = newValue;
        }
      }
    },
    generalDetailsValid: {
      immediate: true,
      handler() {
        this.$emit("pageValidityChange", this.generalDetailsValid);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.general-details {
  padding: 24px;
}

h4 {
  padding-bottom: 4px;
}

.general-details-form {
  align-items: stretch;
  display: flex;
  flex-flow: row wrap;
  background: #ffffff;
  border-radius: 8px;
}

.divider {
  margin-top: 16px;
  flex-basis: 100%;
}

.full-width-field {
  flex: 0 0 100%;
  max-width: 100%;
  padding-right: 12px;
  margin-bottom: 2px;
}
.text-field {
  flex: 0 0 50%;
  max-width: 50%;
  padding-right: 12px;
  margin-bottom: 2px;
}

.phone-field {
  padding-right: 12px;
  margin-bottom: 12px;
  margin-top: -9px;
  height: 84px;
  flex: 1;
  max-width: calc(100% - 44px);
}

.phone-field::v-deep .Vlt-input input {
  transition: unset;
}

.phone-field.Vlt-form__element--error::v-deep input {
  background-color: var(--vvd-color-alert-20);
}

.warning-message-wrapper {
  box-sizing: border-box;
  background: #fdf5d4;
  border: 1px solid #be5702;
  border-radius: 6px;
  padding: 20px;
  margin-right: 12px;
  display: flex;
  width: 100%;
  margin-top: 16px;
  margin-bottom: 16px;
}

.info-message-wrapper {
  box-sizing: border-box;
  background: #E8F4FB;
  border: 1px solid #0276D5;
  border-radius: 6px;
  padding: 20px;
  margin-right: 12px;
  display: flex;
  margin-top: 16px;
  margin-bottom: 16px;
  width: 100%;
}

.warning-icon {
  color: #be5702;
  height: 18.75px;
  width: 20px;
  margin-right: 16px;
}

.email-verification-info {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 20px;
  background-color: #e6f3ff;
  border: 1px solid #0066cc;
  border-radius: 6px;
}

.info-icon {
  color: #0066cc;
  margin-right: 8px;
}

#canada-tax-id-wrapper {
  position: relative;
  flex: 1;
  display: flex;
}
#canada-tax-id {
  flex: 1;
  max-width: unset;
}
#canada-tax-id-tooltip {
  --tooltip-inline-size: 370px;
}
#help-icon {
  position: absolute;
  right: 16px;
  top: 28px;
  background: #e6e6e6;
}

.tax-id-wrapper {
  flex: 0 0 50%;
  position: relative;

  .text-field {
    align-items: stretch;
    max-width: 100%;
  }

  .tax-id-helper {
    position: absolute;
    right: 15px;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    text-decoration-line: underline;
    color: #871eff;
    cursor: pointer;
  }
}
#stock-exchange-select {
  margin-bottom: 20px;
}

.entity-type-radio-group {
  flex: 0 0 100%;
  margin-bottom: 20px;

  span {
    display: block;
    margin-bottom: 10px;
  }

  .radio-options-container {
    display: flex;
    flex-wrap: wrap;
    gap: 40px;
  }

  .radio-option {
    display: flex;
    align-items: center;

    label {
      margin-left: 8px;
      cursor: pointer;
      user-select: none;
    }
  }
}

.tooltip-wrapper-div {
  flex: 0 0 50%;
  display: flex;
  flex-direction: row;
  align-items: center;

  .text-field {
    align-items: stretch;
    flex: 1;
    max-width: calc(100% - 44px);
  }
}

</style>